/**
 * Frontend domain — primitives. Split out of ComponentDomain.ts on 2026-08-15; that barrel went in
 * strict-ports FE6b (2026-10-04) and its importers read this module directly. `GameId`,
 * `CupAnimationState` and `RoundOutcome` went in frontend-domain-oo P0 (§28.267): their one reader
 * was the dead round model in `entities.ts`.
 *
 * Mostly types, plus the enums and the one constant below, which do emit. (This header read
 * "Types only — no runtime cost either way" until 2026-09-03; the enums have always compiled to
 * real objects, so that was never true — see §28.88 on comments asserting mechanisms.) The value
 * is that a reader can see what a consumer actually needs, and that the four areas can now move
 * independently.
 */
// -----------------------------------------------------------------------------
// SECTION 1: PRIMITIVE VALUE TYPES & ENUMS
// -----------------------------------------------------------------------------

/**
 * Lives a player starts a game with — the frontend's single spelling of the number.
 *
 * The BACKEND owns this rule (`PlayerLives.INITIAL_LIVES`, which validates and defaults against
 * it); this is the client's copy of a value it cannot import across the workspace boundary, and
 * the two must agree. It exists because the literal `6` was written in four places here —
 * playerLivesSlice's two, and ChallengerSelection's two — none of which could see each other.
 *
 * Every use is a FALLBACK for "the lives map has no entry for this player yet", which is why the
 * server's real value always wins when one has arrived.
 */
export const INITIAL_LIVES = 6;

export type PlayerId = string;
export type RoomId = string;

export enum GamePhase {
  IDLE = 'IDLE',
  LOBBY = 'LOBBY',
  STARTING = 'STARTING',
  ROLLING = 'ROLLING',
  PEEKING = 'PEEKING',
  CLAIMING = 'CLAIMING',
  DECIDING = 'DECIDING',
  REVEALING = 'REVEALING',
  ROUND_END = 'ROUND_END',
  GAME_OVER = 'GAME_OVER',
}

export enum PlayerStatus {
  WAITING = 'WAITING',
  ACTIVE = 'ACTIVE',
  SPECTATING = 'SPECTATING',
  DISCONNECTED = 'DISCONNECTED',
  ELIMINATED = 'ELIMINATED',
}

export enum MonetizationTrack {
  FREE = 'FREE',
  PREMIUM = 'PREMIUM',
}

// -----------------------------------------------------------------------------
